// v640.5: Configurable keybinds — single source of truth for letter-key actions.
// Movement (WASD/arrows), Esc, Enter, and numeric submenu shortcuts (1-9) stay
// hardcoded. Only single-letter action keys are user-rebindable.
import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'loria_keybinds_v1';

// Action id → default key (lowercase). Keep these short labels for the UI.
export const DEFAULT_KEYBINDS = {
  // Overworld panel toggles
  charSheet:    'c',
  inventory:    'i',
  shop:         'b',
  market:       'm',
  arena:        'n',
  skillTree:    't',
  compendium:   'j',
  guide:        'g',
  // Combat actions (classic mode)
  attack:       'a',
  combatSpells: 's',
  abilities:    'd',
  combatItems:  'i',
  run:          'r',
  endTurn:      'e',
  repeatLast:   'q',
};

export const KEYBIND_LABELS = {
  charSheet:    'Character Sheet',
  inventory:    'Inventory',
  shop:         'Shop (on shop tile)',
  market:       'Market (on market tile)',
  arena:        'Arena (on arena tile)',
  skillTree:    'Skill Tree',
  compendium:   'Compendium / Bestiary',
  guide:        'Progression Guide',
  attack:       'Attack (combat)',
  combatSpells: 'Open Spells (combat)',
  abilities:    'Open Abilities (combat)',
  combatItems:  'Open Items (combat)',
  run:          'Run / Flee (combat)',
  endTurn:      'End Turn (deck combat)',
  repeatLast:   'Repeat Last Action (combat)',
};

export const KEYBIND_GROUPS = [
  { label: 'Overworld', ids: ['charSheet', 'inventory', 'shop', 'market', 'arena', 'skillTree', 'compendium', 'guide'] },
  { label: 'Combat', ids: ['attack', 'combatSpells', 'abilities', 'combatItems', 'run', 'endTurn', 'repeatLast'] },
];

function readBinds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_KEYBINDS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_KEYBINDS, ...parsed };
  } catch (e) {
    return { ...DEFAULT_KEYBINDS };
  }
}

function writeBinds(binds) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(binds));
  } catch (e) {
    /* ignore quota / private mode */
  }
  // Notify any other listeners in this tab
  window.dispatchEvent(new CustomEvent('loria-keybinds-changed', { detail: binds }));
}

/**
 * Returns the current keybind map plus mutators.
 *   const { binds, setBind, resetBinds, matches } = useKeybinds();
 *   if (matches(e, 'attack')) { ... }
 */
export default function useKeybinds() {
  const [binds, setBinds] = useState(readBinds);

  useEffect(() => {
    const onChange = (e) => {
      if (e && e.detail) setBinds(e.detail);
      else setBinds(readBinds());
    };
    window.addEventListener('loria-keybinds-changed', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('loria-keybinds-changed', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);

  const setBind = useCallback((id, key) => {
    if (!Object.prototype.hasOwnProperty.call(DEFAULT_KEYBINDS, id)) return;
    const next = { ...readBinds(), [id]: (key || '').toLowerCase().slice(0, 1) || DEFAULT_KEYBINDS[id] };
    writeBinds(next);
    setBinds(next);
  }, []);

  const resetBinds = useCallback(() => {
    writeBinds({ ...DEFAULT_KEYBINDS });
    setBinds({ ...DEFAULT_KEYBINDS });
  }, []);

  const matches = useCallback((eventOrKey, actionId) => {
    const k = typeof eventOrKey === 'string'
      ? eventOrKey.toLowerCase()
      : (eventOrKey && eventOrKey.key ? eventOrKey.key.toLowerCase() : '');
    return k === binds[actionId];
  }, [binds]);

  return { binds, setBind, resetBinds, matches };
}

// Module-level helper for non-hook contexts (handler refs, etc.)
export function getKeybindsSnapshot() {
  return readBinds();
}
